import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ClipboardList, FileText, MessageSquare, Wrench, Mic } from "lucide-react";

export default function Tools() {
  return (
    <div className="space-y-5" data-testid="tools-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Tools</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">CV Builder · Interview Prep · Documents · Safety Calculators</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <Link to="/tools/cv" data-testid="tool-cv" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <FileText className="h-6 w-6 text-amber-500" />
          <div className="mt-3 font-semibold">CV Builder</div>
          <div className="text-sm text-slate-500">Build & export your HSE CV.</div>
        </Link>
        <Link to="/tools/interview" data-testid="tool-interview" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <MessageSquare className="h-6 w-6 text-amber-500" />
          <div className="mt-3 font-semibold">Interview Prep & Simulator</div>
          <div className="text-sm text-slate-500">Role-based Q&A with model answers.</div>
        </Link>
        <Link to="/tools/simulator" data-testid="tool-simulator" className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 hover:border-amber-500 transition-colors">
          <Mic className="h-6 w-6 text-amber-500" />
          <div className="mt-3 font-semibold">Spoken Interview Simulator</div>
          <div className="text-sm text-slate-500">Practice out loud — auto-transcribe and score your answer.</div>
        </Link>
        <Link to="/tools/documents" data-testid="tool-documents" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <ClipboardList className="h-6 w-6 text-amber-500" />
          <div className="mt-3 font-semibold">Documents & Templates</div>
          <div className="text-sm text-slate-500">RAMS, PTW, JSA, checklists.</div>
        </Link>
        <Link to="/tools/risk" data-testid="tool-risk" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <Calculator className="h-6 w-6 text-amber-500" />
          <div className="mt-3 font-semibold">Risk Matrix Calculator</div>
          <div className="text-sm text-slate-500">Interactive 5x5 risk assessment.</div>
        </Link>
      </div>
    </div>
  );
}

export function RiskMatrix() {
  const [likelihood, setLikelihood] = useState(3);
  const [severity, setSeverity] = useState(3);
  const score = likelihood * severity;
  const level = score >= 15 ? { label: "High", color: "bg-rose-500" } : score >= 8 ? { label: "Medium", color: "bg-amber-500" } : { label: "Low", color: "bg-emerald-500" };

  return (
    <div className="max-w-3xl space-y-5" data-testid="risk-matrix-page">
      <h1 className="text-3xl font-extrabold tracking-tight">Risk Matrix</h1>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-slate-900">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Likelihood (1-5)</div>
            <input data-testid="risk-likelihood" type="range" min="1" max="5" value={likelihood} onChange={(e)=>setLikelihood(parseInt(e.target.value))} className="w-full accent-amber-500" />
            <div className="mono font-bold text-xl">{likelihood}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Severity (1-5)</div>
            <input data-testid="risk-severity" type="range" min="1" max="5" value={severity} onChange={(e)=>setSeverity(parseInt(e.target.value))} className="w-full accent-amber-500" />
            <div className="mono font-bold text-xl">{severity}</div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Risk score</div>
            <div className="mono font-extrabold text-4xl">{score}</div>
          </div>
          <div data-testid="risk-level" className={`px-4 py-2 rounded-full text-white font-semibold ${level.color}`}>{level.label}</div>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl">
          <table className="w-full text-center text-xs">
            <tbody>
              {[5,4,3,2,1].map(s => (
                <tr key={s}>
                  {[1,2,3,4,5].map(l => {
                    const v = s*l;
                    const bg = v >= 15 ? "bg-rose-500/70" : v >= 8 ? "bg-amber-500/70" : "bg-emerald-500/70";
                    const hi = l === likelihood && s === severity;
                    return <td key={l} className={`h-10 ${bg} text-white font-bold ${hi ? "ring-4 ring-slate-900 dark:ring-white" : ""}`}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-5 mt-1 text-xs text-slate-500">
            {[1,2,3,4,5].map(x => <div key={x}>L{x}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
